import type { CandidateLevel, InterviewQuestion, InterviewType } from "@scoach/types";
import { useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";

import { DashHeader } from "../../dashboard/components/DashHeader.tsx";
import { interviewApi } from "../api.ts";
import { QuestionBrowser } from "./QuestionBrowser.tsx";

const LEVELS: CandidateLevel[] = ["L3", "L4", "L5", "L6", "L6+"];

interface ChecklistItem {
  id: string;
  text: string;
  origin: "master" | "manual" | "ai";
}

/** Same predicate as the backend /interviews/questions filter — "recommended". */
function isRecommended(q: InterviewQuestion, level: string, isManager: boolean): boolean {
  const levels = (q.levels ?? (q.level ? [q.level] : [])).map((l) => l.toLowerCase());
  if (levels.length && !levels.includes(level.toLowerCase())) return false;
  if (q.managerOnly && !isManager) return false;
  return true;
}

export function InterviewSetupScreen({ meetingId }: { meetingId: string }) {
  const nav = useNavigate();

  const [types, setTypes] = useState<InterviewType[]>([]);
  const [candidateName, setCandidateName] = useState("");
  const [level, setLevel] = useState<CandidateLevel>("L4");
  const [roleType, setRoleType] = useState("");
  const [isManager, setIsManager] = useState(false);
  const [typeId, setTypeId] = useState("");
  const [items, setItems] = useState<ChecklistItem[]>([]);
  const [allQuestions, setAllQuestions] = useState<InterviewQuestion[]>([]);
  const [cvName, setCvName] = useState<string | null>(null);
  const [cvBusy, setCvBusy] = useState(false);
  const [loadingQs, setLoadingQs] = useState(false);
  const [starting, setStarting] = useState(false);
  const [newQuestion, setNewQuestion] = useState("");

  const selectedType = types.find((t) => t.id === typeId) ?? null;

  useEffect(() => {
    interviewApi
      .fetchTypes()
      .then(setTypes)
      .catch(() => setTypes([]));
  }, []);

  // Load the FULL question bank for the type, and pre-check level-recommended ones.
  const loadQuestions = useCallback(async () => {
    if (!selectedType) return;
    setLoadingQs(true);
    try {
      const qs = await interviewApi.fetchAllQuestionsForType(selectedType.recommendedTracks);
      setAllQuestions(qs);
      const recommended = qs.filter((q) => isRecommended(q, level, isManager));
      setItems(recommended.map((q) => ({ id: q.id, text: q.text, origin: "master" })));
    } finally {
      setLoadingQs(false);
    }
  }, [selectedType, level, isManager]);

  useEffect(() => {
    if (selectedType) void loadQuestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeId]);

  // Re-seed the master (bank) selection whenever the level/track changes so the
  // pre-selected questions always match the chosen level. Manual/AI additions
  // are preserved. (loadQuestions only re-runs on type change.)
  useEffect(() => {
    if (allQuestions.length === 0) return;
    const recommended = allQuestions.filter((q) => isRecommended(q, level, isManager));
    setItems((prev) => [
      ...recommended.map((q) => ({ id: q.id, text: q.text, origin: "master" as const })),
      ...prev.filter((i) => i.origin !== "master"),
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [level, isManager]);

  const recommendedIds = useMemo(
    () => new Set(allQuestions.filter((q) => isRecommended(q, level, isManager)).map((q) => q.id)),
    [allQuestions, level, isManager],
  );
  const selectedIds = useMemo(() => new Set(items.map((i) => i.id)), [items]);

  function toggleQuestion(q: InterviewQuestion) {
    setItems((prev) =>
      prev.some((i) => i.id === q.id)
        ? prev.filter((i) => i.id !== q.id)
        : [...prev, { id: q.id, text: q.text, origin: "master" }],
    );
  }

  // Bulk select/unselect the given (usually the currently-filtered) questions.
  // Only affects master (bank) items; manual/AI additions are untouched.
  function bulkToggle(qs: InterviewQuestion[], select: boolean) {
    setItems((prev) => {
      const ids = new Set(qs.map((q) => q.id));
      if (!select) return prev.filter((i) => !ids.has(i.id));
      const existing = new Set(prev.map((i) => i.id));
      const additions = qs
        .filter((q) => !existing.has(q.id))
        .map((q) => ({ id: q.id, text: q.text, origin: "master" as const }));
      return [...prev, ...additions];
    });
  }

  async function handleCv(file: File) {
    setCvBusy(true);
    try {
      // Persist a draft interview block first so /interviews/:id/cv can attach to it.
      await interviewApi.patchMeeting(meetingId, {
        meetingType: "interview",
        interview: buildMeta(),
      });
      const res = await interviewApi.uploadCv(meetingId, file);
      setCvName(res.cvFileName);
    } catch (err) {
      alert(`CV upload failed: ${(err as Error).message}`);
    } finally {
      setCvBusy(false);
    }
  }

  async function getMore() {
    setLoadingQs(true);
    try {
      await interviewApi.patchMeeting(meetingId, {
        meetingType: "interview",
        interview: buildMeta(),
      });
      const more = await interviewApi.suggestQuestions(meetingId, {
        existingQuestions: items.map((i) => i.text),
        count: 3,
      });
      setItems((prev) => [
        ...prev,
        ...more.map((text) => ({ id: `ai-${crypto.randomUUID()}`, text, origin: "ai" as const })),
      ]);
    } catch (err) {
      alert(`Could not fetch more questions: ${(err as Error).message}`);
    } finally {
      setLoadingQs(false);
    }
  }

  function buildMeta() {
    return {
      candidateLevel: level,
      roleType,
      isManager,
      interviewTypeId: typeId,
      selectedQuestionIds: items.filter((i) => i.origin === "master").map((i) => i.id),
      // Persist every queued question WITH its text (incl. master) so the live
      // checklist + summary don't have to re-resolve master ids.
      customQuestions: items.map((i) => ({ id: i.id, text: i.text, origin: i.origin })),
      ...(candidateName ? { candidateName } : {}),
    };
  }

  async function start() {
    if (!typeId) {
      alert("Pick an interview type first.");
      return;
    }
    if (!candidateName.trim()) {
      alert("Enter the candidate name first.");
      return;
    }
    setStarting(true);
    try {
      await interviewApi.patchMeeting(meetingId, {
        meetingType: "interview",
        title: `Interview — ${candidateName.trim()} · ${selectedType?.name ?? ""}`.replace(
          / · $/,
          "",
        ),
        interview: buildMeta(),
      });
      nav({ to: "/interviews/$id/live", params: { id: meetingId } });
    } catch (err) {
      alert(`Could not start: ${(err as Error).message}`);
      setStarting(false);
    }
  }

  return (
    <div className="dash">
      <DashHeader />
      <div style={{ maxWidth: 900, margin: "0 auto", padding: 24 }}>
        <h1 style={{ marginBottom: 4 }}>New interview</h1>
        <p style={{ color: "var(--text-3)", marginTop: 0 }}>
          Set up the candidate and pick the interview type. The scorecard is loaded from the
          selected type.
        </p>

        {types.length === 0 && (
          <div
            style={{
              padding: "10px 14px",
              borderRadius: 8,
              background: "rgba(249,171,0,.12)",
              border: "1px solid #f9ab00",
              fontSize: 13,
              marginBottom: 12,
            }}
          >
            No interview types are configured yet, so <strong>Start interview</strong> stays
            disabled. An admin needs to upload the interview matrix first (Admin → “Manage interview
            data”).
          </div>
        )}

        <section style={cardStyle}>
          <h3 style={{ marginTop: 0 }}>Candidate & role</h3>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <Field label="Candidate name (required)">
              <input
                style={inputStyle}
                value={candidateName}
                onChange={(e) => setCandidateName(e.target.value)}
                placeholder="Shown in the interview name; anonymized to “TC” in the evaluation"
              />
            </Field>
            <Field label="Role type">
              <input
                style={inputStyle}
                value={roleType}
                onChange={(e) => setRoleType(e.target.value)}
                placeholder="e.g. Field Sales, Software Engineer"
              />
            </Field>
            <Field label="Candidate level">
              <select
                style={inputStyle}
                value={level}
                onChange={(e) => setLevel(e.target.value as CandidateLevel)}
              >
                {LEVELS.map((l) => (
                  <option key={l} value={l}>
                    {l}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Track">
              <label style={{ display: "inline-flex", gap: 8, alignItems: "center", marginTop: 8 }}>
                <input
                  type="checkbox"
                  checked={isManager}
                  onChange={(e) => setIsManager(e.target.checked)}
                />
                Manager / leadership track
              </label>
            </Field>
            <Field label="Interview type">
              <select style={inputStyle} value={typeId} onChange={(e) => setTypeId(e.target.value)}>
                <option value="">Select…</option>
                {types.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Candidate CV (optional)">
              <input
                type="file"
                accept=".pdf,.doc,.docx"
                disabled={cvBusy}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void handleCv(f);
                }}
              />
              {cvBusy && <span style={{ fontSize: 12, color: "var(--text-3)" }}>Parsing…</span>}
              {cvName && <span style={{ fontSize: 12, color: "#34a853" }}>✓ {cvName}</span>}
            </Field>
          </div>
          {selectedType && (
            <div style={{ marginTop: 12, fontSize: 13, color: "var(--text-2)" }}>
              <strong>Scorecard:</strong>{" "}
              {selectedType.pillars.map((p) => p.name).join(" · ") || "(no pillars configured)"}
            </div>
          )}
        </section>

        <section style={cardStyle}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h3 style={{ margin: 0 }}>Question bank — {items.length} selected</h3>
            <button
              type="button"
              className="pill-btn"
              onClick={getMore}
              disabled={loadingQs || !typeId}
            >
              Suggest more (AI)
            </button>
          </div>
          {!typeId && (
            <p style={{ color: "var(--text-3)" }}>Pick an interview type to load its questions.</p>
          )}
          {loadingQs && <p style={{ color: "var(--text-3)" }}>Loading…</p>}
          {typeId && !loadingQs && (
            <>
              <p style={{ fontSize: 13, color: "var(--text-3)", marginTop: 4 }}>
                Browse the full list and tick the questions you want. Level-recommended ones (★) are
                pre-selected.
              </p>
              <QuestionBrowser
                questions={allQuestions}
                selectedIds={selectedIds}
                recommendedIds={recommendedIds}
                onToggle={toggleQuestion}
                onBulk={bulkToggle}
              />
            </>
          )}

          {/* AI-suggested / manually-added questions (not in the bank). */}
          {items.some((i) => i.origin !== "master") && (
            <div style={{ marginTop: 14 }}>
              <div
                style={{ fontSize: 12, fontWeight: 600, color: "var(--text-2)", marginBottom: 6 }}
              >
                Added questions
              </div>
              <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                {items
                  .filter((i) => i.origin !== "master")
                  .map((it) => (
                    <li
                      key={it.id}
                      style={{ display: "flex", gap: 8, alignItems: "center", padding: "6px 0" }}
                    >
                      <input
                        style={{ ...inputStyle, flex: 1 }}
                        value={it.text}
                        onChange={(e) =>
                          setItems((prev) =>
                            prev.map((x) => (x.id === it.id ? { ...x, text: e.target.value } : x)),
                          )
                        }
                      />
                      <span style={{ fontSize: 11, color: "#7627bb" }}>{it.origin}</span>
                      <button
                        type="button"
                        className="icon-btn"
                        onClick={() => setItems((prev) => prev.filter((x) => x.id !== it.id))}
                        aria-label="Remove"
                      >
                        ✕
                      </button>
                    </li>
                  ))}
              </ul>
            </div>
          )}

          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <input
              style={{ ...inputStyle, flex: 1 }}
              value={newQuestion}
              onChange={(e) => setNewQuestion(e.target.value)}
              placeholder="Add your own question…"
            />
            <button
              type="button"
              className="pill-btn"
              onClick={() => {
                const t = newQuestion.trim();
                if (!t) return;
                setItems((prev) => [
                  ...prev,
                  { id: `manual-${crypto.randomUUID()}`, text: t, origin: "manual" },
                ]);
                setNewQuestion("");
              }}
            >
              Add
            </button>
          </div>
        </section>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
          <button type="button" className="pill-btn" onClick={() => nav({ to: "/interviews" })}>
            Cancel
          </button>
          <button
            type="button"
            className="pill-btn primary"
            onClick={start}
            disabled={starting || !typeId || !candidateName.trim()}
          >
            {starting ? "Starting…" : "Start interview"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span style={{ fontSize: 12, color: "var(--text-3)" }}>{label}</span>
      {children}
    </label>
  );
}

const cardStyle: React.CSSProperties = {
  background: "var(--surface)",
  border: "1px solid var(--border-1)",
  borderRadius: 12,
  padding: 20,
  margin: "16px 0",
};
const inputStyle: React.CSSProperties = {
  padding: "8px 10px",
  borderRadius: 8,
  border: "1px solid var(--border-1)",
  background: "var(--surface-2)",
  color: "var(--text-1)",
  fontSize: 14,
};
